import { useState } from "react";
import { Link, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit, Calendar, DollarSign, FileText } from "lucide-react";
import { getAppIcon, getAppColor, formatDate, isLicenseExpired, getLicenseStatusColor } from "@/lib/utils";
import { Application, License } from "@shared/schema";
import EditApplicationDialog from "@/components/applications/edit-application-dialog";

type HistoricalRenewalWithLicense = {
  id: number;
  licenseId: number;
  licenseName: string;
  renewalDate: Date;
  previousCost: number;
  newCost: number;
  currency: string;
  notes: string | null;
  invoiceUrl: string | null;
  createdAt: Date;
};

export default function ApplicationDetail() {
  const params = useParams();
  const applicationId = params.id ? parseInt(params.id) : 0;
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Fetch application data
  const { data: application, isLoading: appLoading, error: appError } = useQuery<Application>({
    queryKey: ['/api/applications', applicationId],
    queryFn: async () => {
      if (!applicationId) throw new Error("Application ID is required");
      const res = await fetch(`/api/applications/${applicationId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch application');
      }
      return res.json();
    },
    enabled: !!applicationId,
  });

  // Fetch licenses for this application
  const { data: licenses, isLoading: licensesLoading } = useQuery<License[]>({
    queryKey: ['/api/applications', applicationId, 'licenses'],
    queryFn: async () => {
      if (!applicationId) throw new Error("Application ID is required");
      const res = await fetch(`/api/applications/${applicationId}/licenses`);
      if (!res.ok) {
        throw new Error('Failed to fetch application licenses');
      }
      return res.json();
    },
    enabled: !!applicationId,
  });

  // Fetch historical renewals for this application
  const { data: historicalRenewals = [], isLoading: renewalsLoading } = useQuery<HistoricalRenewalWithLicense[]>({
    queryKey: ['/api/applications', applicationId, 'historical-renewals'],
    queryFn: async () => {
      if (!applicationId) throw new Error("Application ID is required");
      const res = await fetch(`/api/applications/${applicationId}/historical-renewals`);
      if (!res.ok) {
        throw new Error('Failed to fetch historical renewals');
      }
      return res.json();
    },
    enabled: !!applicationId,
  });

  const isLoading = appLoading || licensesLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-64 md:col-span-2" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (appError || !application) {
    return (
      <div className="max-w-screen-md mx-auto my-16 text-center">
        <h2 className="text-2xl font-bold text-red-500 mb-4">Error Loading Application</h2>
        <p className="text-slate-600 mb-8">Unable to load application details. The application may not exist or there was a network error.</p>
        <Button asChild>
          <Link to="/applications">Back to Applications</Link>
        </Button>
      </div>
    );
  }

  // Process application data
  const appIcon = getAppIcon(application.logo);
  const appColor = getAppColor(application.logo);
  const statusColor = application.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800';
  const formattedStatus = application.status.charAt(0).toUpperCase() + application.status.slice(1);
  
  // Calculate application stats
  const totalLicenses = licenses?.length || 0;
  const activeLicenses = licenses?.filter(license => license.status === 'active').length || 0;
  const totalSeats = licenses?.reduce((sum, license) => sum + license.totalSeats, 0) || 0;
  const assignedSeats = licenses?.reduce((sum, license) => sum + license.assignedSeats, 0) || 0;
  const totalCost = licenses?.reduce((sum, license) => sum + license.cost, 0) || 0;

  return (
    <div>
      <EditApplicationDialog 
        applicationId={applicationId}
        isOpen={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
      />
      
      <div className="mb-6 flex items-center justify-between">
        <Button asChild variant="ghost" size="sm">
          <Link to="/applications">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Applications
          </Link>
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setEditDialogOpen(true)}
        >
          <Edit className="mr-2 h-4 w-4" />
          Edit Application
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center">
              <CardTitle className="text-xl font-semibold">Application Details</CardTitle>
              <Badge className={`ml-auto ${statusColor}`}>
                {formattedStatus}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className={`h-16 w-16 rounded ${appColor} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${appIcon} text-2xl`}></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-1">{application.name}</h3>
                  <p className="text-slate-600">{application.vendor}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline">{application.category}</Badge>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <h4 className="text-sm font-medium mb-3">Description</h4>
                <p className="text-slate-600">{application.description || "No description available."}</p>
              </div>

              <div className="pt-4 border-t border-slate-200 grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Added On</h4>
                  <p>{formatDate(application.addedAt)}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Status</h4>
                  <p>{formattedStatus}</p>
                </div>
                {application.website && (
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Website</h4>
                    <a 
                      href={application.website} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-primary hover:underline"
                    >
                      {application.website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">License Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-500 mb-1">Total Licenses</h4>
                <p className="text-2xl font-semibold">{totalLicenses}</p>
              </div>
              
              <div className="border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-500 mb-1">Active Licenses</h4>
                <p className="text-2xl font-semibold">{activeLicenses}</p>
              </div>
              
              <div className="border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-500 mb-1">Total Seats</h4>
                <p className="text-2xl font-semibold">{totalSeats}</p>
                <p className="text-sm text-slate-500">{assignedSeats} assigned ({Math.round((assignedSeats / totalSeats) * 100) || 0}%)</p>
              </div>
              
              <div className="border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-500 mb-1">Monthly Cost</h4>
                <p className="text-2xl font-semibold">${totalCost.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Application Licenses Section */}
      {licenses && licenses.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Licenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Type</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Seats</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Renewal Date</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Cost</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map(license => {
                    const isLifetime = license.billingCycle === 'Lifetime' || license.billingCycle === 'One-Time';
                    const effectivelyExpired = !isLifetime && license.status !== 'discontinued' && isLicenseExpired(license.renewalDate);
                    const displayStatus = effectivelyExpired ? 'expired' : (license.status || 'unknown');

                    return (
                      <tr key={license.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">{license.name}</td>
                        <td className="py-3 px-4">{(license as any).type || license.billingCycle}</td>
                        <td className="py-3 px-4">
                          <Badge className={getLicenseStatusColor(displayStatus)}>
                            {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">{license.assignedSeats} / {license.totalSeats}</td>
                        <td className="py-3 px-4">{formatDate(license.renewalDate)}</td>
                        <td className="py-3 px-4">${license.cost.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right">
                          <Button asChild variant="ghost" size="sm">
                            <Link to={`/licenses/${license.id}`}>
                              Manage
                            </Link>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing History Section */}
      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">Billing History</CardTitle>
            {historicalRenewals.length > 0 && (
              <Badge variant="outline" className="ml-auto">
                {historicalRenewals.length} {historicalRenewals.length === 1 ? 'Renewal' : 'Renewals'}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {renewalsLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : historicalRenewals.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Renewal Date
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">License</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        Previous Cost
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        New Cost
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Notes</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">
                      <div className="flex items-center justify-center gap-1">
                        <FileText className="h-4 w-4" />
                        Invoice
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {historicalRenewals.map((renewal) => (
                    <tr key={renewal.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium">{formatDate(renewal.renewalDate)}</td>
                      <td className="py-3 px-4">
                        <Link to={`/licenses/${renewal.licenseId}`} className="text-primary hover:underline">
                          {renewal.licenseName}
                        </Link>
                      </td>
                      <td className="py-3 px-4">
                        {renewal.currency} {renewal.previousCost.toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        {renewal.currency} {renewal.newCost.toLocaleString()}
                        {renewal.newCost !== renewal.previousCost && (
                          <span className={renewal.newCost > renewal.previousCost ? 'text-red-600 text-xs ml-1' : 'text-green-600 text-xs ml-1'}>
                            ({renewal.newCost > renewal.previousCost ? '+' : ''}
                            {((renewal.newCost - renewal.previousCost) / renewal.previousCost * 100).toFixed(1)}%)
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 max-w-xs truncate text-slate-600">
                        {renewal.notes || '-'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {renewal.invoiceUrl ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8"
                            onClick={() => window.open(`/api/historical-renewals/${renewal.id}/invoice`, '_blank')}
                          >
                            <FileText className="h-4 w-4 text-blue-600" />
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium mb-1">No billing history yet</p>
              <p className="text-xs">Renewal history will appear here when licenses are renewed</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}